(function () {

    var rum = Ya.Rum;

    /*
     * Copyright 2020 Google LLC
     *
     * Licensed under the Apache License, Version 2.0 (the "License");
     * you may not use this file except in compliance with the License.
     * You may obtain a copy of the License at
     *
     *     https://www.apache.org/licenses/LICENSE-2.0
     *
     * Unless required by applicable law or agreed to in writing, software
     * distributed under the License is distributed on an "AS IS" BASIS,
     * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
     * See the License for the specific language governing permissions and
     * limitations under the License.
     */
    var bfcacheRestoreTime = -1;
    var getBFCacheRestoreTime = function getBFCacheRestoreTime() {
      return bfcacheRestoreTime;
    };
    var onBFCacheRestore = function onBFCacheRestore(cb) {
      addEventListener('pageshow', function (event) {
        if (event.persisted) {
          bfcacheRestoreTime = event.timeStamp;
          cb(event);
        }
      }, true);
    };

    /*
     * Copyright 2020 Google LLC
     *
     * Licensed under the Apache License, Version 2.0 (the "License");
     * you may not use this file except in compliance with the License.
     * You may obtain a copy of the License at
     *
     *     https://www.apache.org/licenses/LICENSE-2.0
     *
     * Unless required by applicable law or agreed to in writing, software
     * distributed under the License is distributed on an "AS IS" BASIS,
     * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
     * See the License for the specific language governing permissions and
     * limitations under the License.
     */
    var bindReporter = function bindReporter(callback, metric, reportAllChanges) {
      var prevValue;
      var delta;
      return function (forceReport) {
        if (metric.value >= 0) {
          if (forceReport || reportAllChanges) {
            delta = metric.value - (prevValue || 0);
            // Report the metric if there's a non-zero delta or if no previous
            // value exists (which can happen in the case of the document becoming
            // hidden when the metric value is 0).
            // See: https://github.com/GoogleChrome/web-vitals/issues/14
            if (delta || prevValue === undefined) {
              prevValue = metric.value;
              metric.delta = delta;
              callback(metric);
            }
          }
        }
      };
    };

    /*
     * Copyright 2020 Google LLC
     *
     * Licensed under the Apache License, Version 2.0 (the "License");
     * you may not use this file except in compliance with the License.
     * You may obtain a copy of the License at
     *
     *     https://www.apache.org/licenses/LICENSE-2.0
     *
     * Unless required by applicable law or agreed to in writing, software
     * distributed under the License is distributed on an "AS IS" BASIS,
     * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
     * See the License for the specific language governing permissions and
     * limitations under the License.
     */
    /**
     * Performantly generate a unique, 30-char string by combining a version
     * number, the current timestamp with a 13-digit number integer.
     * @return {string}
     */
    var generateUniqueID = function generateUniqueID() {
      return "v3-".concat(Date.now(), "-").concat(Math.floor(Math.random() * (9e12 - 1)) + 1e12);
    };

    /*
     * Copyright 2022 Google LLC
     *
     * Licensed under the Apache License, Version 2.0 (the "License");
     * you may not use this file except in compliance with the License.
     * You may obtain a copy of the License at
     *
     *     https://www.apache.org/licenses/LICENSE-2.0
     *
     * Unless required by applicable law or agreed to in writing, software
     * distributed under the License is distributed on an "AS IS" BASIS,
     * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
     * See the License for the specific language governing permissions and
     * limitations under the License.
     */
    var getNavigationEntryFromPerformanceTiming = function getNavigationEntryFromPerformanceTiming() {
      var timing = performance.timing;
      var type = performance.navigation.type;
      var navigationEntry = {
        entryType: 'navigation',
        startTime: 0,
        type: type == 2 ? 'back_forward' : type === 1 ? 'reload' : 'navigate'
      };
      for (var key in timing) {
        if (key !== 'navigationStart' && key !== 'toJSON') {
          navigationEntry[key] = Math.max(timing[key] - timing.navigationStart, 0);
        }
      }
      return navigationEntry;
    };
    var getNavigationEntry = function getNavigationEntry() {
      if (window.__WEB_VITALS_POLYFILL__) {
        return window.performance && (performance.getEntriesByType && performance.getEntriesByType('navigation')[0] || getNavigationEntryFromPerformanceTiming());
      } else {
        return window.performance && performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
      }
    };

    /*
     * Copyright 2022 Google LLC
     *
     * Licensed under the Apache License, Version 2.0 (the "License");
     * you may not use this file except in compliance with the License.
     * You may obtain a copy of the License at
     *
     *     https://www.apache.org/licenses/LICENSE-2.0
     *
     * Unless required by applicable law or agreed to in writing, software
     * distributed under the License is distributed on an "AS IS" BASIS,
     * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
     * See the License for the specific language governing permissions and
     * limitations under the License.
     */
    var getActivationStart = function getActivationStart() {
      var navEntry = getNavigationEntry();
      return navEntry && navEntry.activationStart || 0;
    };

    /*
     * Copyright 2020 Google LLC
     *
     * Licensed under the Apache License, Version 2.0 (the "License");
     * you may not use this file except in compliance with the License.
     * You may obtain a copy of the License at
     *
     *     https://www.apache.org/licenses/LICENSE-2.0
     *
     * Unless required by applicable law or agreed to in writing, software
     * distributed under the License is distributed on an "AS IS" BASIS,
     * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
     * See the License for the specific language governing permissions and
     * limitations under the License.
     */
    var initMetric = function initMetric(name, value) {
      var navEntry = getNavigationEntry();
      var navigationType = 'navigate';
      if (getBFCacheRestoreTime() >= 0) {
        navigationType = 'back-forward-cache';
      } else if (navEntry) {
        if (document.prerendering || getActivationStart() > 0) {
          navigationType = 'prerender';
        } else if (document.wasDiscarded) {
          navigationType = 'restore';
        } else {
          navigationType = navEntry.type.replace(/_/g, '-');
        }
      }
      return {
        name: name,
        value: typeof value === 'undefined' ? -1 : value,
        rating: 'good',
        delta: 0,
        entries: [],
        id: generateUniqueID(),
        navigationType: navigationType
      };
    };

    /*
     * Copyright 2020 Google LLC
     *
     * Licensed under the Apache License, Version 2.0 (the "License");
     * you may not use this file except in compliance with the License.
     * You may obtain a copy of the License at
     *
     *     https://www.apache.org/licenses/LICENSE-2.0
     *
     * Unless required by applicable law or agreed to in writing, software
     * distributed under the License is distributed on an "AS IS" BASIS,
     * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
     * See the License for the specific language governing permissions and
     * limitations under the License.
     */
    /**
     * Takes a performance entry type and a callback function, and creates a
     * `PerformanceObserver` instance that will observe the specified entry type
     * with buffering enabled and call the callback _for each entry_.
     *
     * This function also feature-detects entry support and wraps the logic in a
     * try/catch to avoid errors in unsupporting browsers.
     */
    var observe = function observe(type, callback, opts) {
      try {
        if (PerformanceObserver.supportedEntryTypes.includes(type)) {
          var po = new PerformanceObserver(function (list) {
            // Delay by a microtask to workaround a bug in Safari where the
            // callback is invoked immediately, rather than in a separate task.
            // See: https://github.com/GoogleChrome/web-vitals/issues/277
            Promise.resolve().then(function () {
              callback(list.getEntries());
            });
          });
          po.observe(Object.assign({
            type: type,
            buffered: true
          }, opts || {}));
          return po;
        }
      } catch (e) {
        // Do nothing.
      }
      return;
    };

    /*
     * Copyright 2020 Google LLC
     *
     * Licensed under the Apache License, Version 2.0 (the "License");
     * you may not use this file except in compliance with the License.
     * You may obtain a copy of the License at
     *
     *     https://www.apache.org/licenses/LICENSE-2.0
     *
     * Unless required by applicable law or agreed to in writing, software
     * distributed under the License is distributed on an "AS IS" BASIS,
     * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
     * See the License for the specific language governing permissions and
     * limitations under the License.
     */
    var onHidden = function onHidden(cb, once) {
      var onHiddenOrPageHide = function onHiddenOrPageHide(event) {
        if (event.type === 'pagehide' || document.visibilityState === 'hidden') {
          cb(event);
          if (once) {
            removeEventListener('visibilitychange', onHiddenOrPageHide, true);
            removeEventListener('pagehide', onHiddenOrPageHide, true);
          }
        }
      };
      addEventListener('visibilitychange', onHiddenOrPageHide, true);
      // Some browsers have buggy implementations of visibilitychange,
      // so we use pagehide in addition, just to be safe.
      addEventListener('pagehide', onHiddenOrPageHide, true);
    };

    /*
     * Copyright 2022 Google LLC
     *
     * Licensed under the Apache License, Version 2.0 (the "License");
     * you may not use this file except in compliance with the License.
     * You may obtain a copy of the License at
     *
     *     https://www.apache.org/licenses/LICENSE-2.0
     *
     * Unless required by applicable law or agreed to in writing, software
     * distributed under the License is distributed on an "AS IS" BASIS,
     * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
     * See the License for the specific language governing permissions and
     * limitations under the License.
     */
    var interactionCountEstimate = 0;
    var minKnownInteractionId = Infinity;
    var maxKnownInteractionId = 0;
    var updateEstimate = function updateEstimate(entries) {
      entries.forEach(function (e) {
        if (e.interactionId) {
          minKnownInteractionId = Math.min(minKnownInteractionId, e.interactionId);
          maxKnownInteractionId = Math.max(maxKnownInteractionId, e.interactionId);
          interactionCountEstimate = maxKnownInteractionId ? (maxKnownInteractionId - minKnownInteractionId) / 7 + 1 : 0;
        }
      });
    };
    var po;
    /**
     * Returns the `interactionCount` value using the native API (if available)
     * or the polyfill estimate in this module.
     */
    var getInteractionCount = function getInteractionCount() {
      return po ? interactionCountEstimate : performance.interactionCount || 0;
    };
    /**
     * Feature detects native support or initializes the polyfill if needed.
     */
    var initInteractionCountPolyfill = function initInteractionCountPolyfill() {
      if ('interactionCount' in performance || po) return;
      po = observe('event', updateEstimate, {
        type: 'event',
        buffered: true,
        durationThreshold: 0
      });
    };

    /*
     * Copyright 2022 Google LLC
     *
     * Licensed under the Apache License, Version 2.0 (the "License");
     * you may not use this file except in compliance with the License.
     * You may obtain a copy of the License at
     *
     *     https://www.apache.org/licenses/LICENSE-2.0
     *
     * Unless required by applicable law or agreed to in writing, software
     * distributed under the License is distributed on an "AS IS" BASIS,
     * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
     * See the License for the specific language governing permissions and
     * limitations under the License.
     */
    var whenActivated = function whenActivated(callback) {
      if (document.prerendering) {
        addEventListener('prerenderingchange', function () {
          return callback();
        }, true);
      } else {
        callback();
      }
    };

    /*
     * Copyright 2022 Google LLC
     *
     * Licensed under the Apache License, Version 2.0 (the "License");
     * you may not use this file except in compliance with the License.
     * You may obtain a copy of the License at
     *
     *     https://www.apache.org/licenses/LICENSE-2.0
     *
     * Unless required by applicable law or agreed to in writing, software
     * distributed under the License is distributed on an "AS IS" BASIS,
     * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
     * See the License for the specific language governing permissions and
     * limitations under the License.
     */
    // Used to store the interaction count after a bfcache restore, since p98
    // interaction latencies should only consider the current navigation.
    var prevInteractionCount = 0;
    var metric;
    /**
     * Returns the interaction count since the last bfcache restore (or for the
     * full page lifecycle if there were no bfcache restores).
     */
    var getInteractionCountForNavigation = function getInteractionCountForNavigation() {
      return getInteractionCount() - prevInteractionCount;
    };
    // To prevent unnecessary memory usage on pages with lots of interactions,
    // store at most 10 of the longest interactions to consider as INP candidates.
    var MAX_INTERACTIONS_TO_CONSIDER = 10;
    // A list of longest interactions on the page (by latency) sorted so the
    // longest one is first. The list is as most MAX_INTERACTIONS_TO_CONSIDER long.
    var longestInteractionList = [];
    // A mapping of longest interactions by their interaction ID.
    // This is used for faster lookup.
    var longestInteractionMap = {};
    /**
     * Takes a performance entry and adds it to the list of worst interactions
     * if its duration is long enough to make it among the worst. If the
     * entry is part of an existing interaction, it is merged and the latency
     * and entries list is updated as needed.
     */
    var processEntry = function processEntry(entry) {
      // The least-long of the 10 longest interactions.
      var minLongestInteraction = longestInteractionList[longestInteractionList.length - 1];
      var existingInteraction = longestInteractionMap[entry.interactionId];
      // Only process the entry if it's possibly one of the ten longest,
      // or if it's part of an existing interaction.
      if (existingInteraction || longestInteractionList.length < MAX_INTERACTIONS_TO_CONSIDER || entry.duration > minLongestInteraction.latency) {
        // If the interaction already exists, update it. Otherwise create one.
        if (existingInteraction) {
          existingInteraction.entries.push(entry);
          existingInteraction.latency = Math.max(existingInteraction.latency, entry.duration);
        } else {
          var interaction = {
            id: entry.interactionId,
            latency: entry.duration,
            entries: [entry]
          };
          longestInteractionMap[interaction.id] = interaction;
          longestInteractionList.push(interaction);
        }
        // Sort the entries by latency (descending) and keep only the top ten.
        longestInteractionList.sort(function (a, b) {
          return b.latency - a.latency;
        });
        longestInteractionList.splice(MAX_INTERACTIONS_TO_CONSIDER).forEach(function (i) {
          delete longestInteractionMap[i.id];
        });
      }
    };
    /**
     * RUM_CHANGE: Добавлен метод для сброса состояния счетчика
     * Resets internal onINP state.
     *
     * Could be used in reinitialization cases(BFC, SPA).
     */
    var resetINPState = function resetINPState() {
      longestInteractionList = [];
      longestInteractionMap = {};
      // не создаем новый объект, чтобы не вызывать bindReporter еще раз (у старого ссылка в замыкании)
      metric.value = -1;
      metric.delta = 0;
      metric.entries = [];
      metric.id = generateUniqueID();
    };
    /**
     * Returns the estimated p98 longest interaction based on the stored
     * interaction candidates and the interaction count for the current page.
     */
    var estimateP98LongestInteraction = function estimateP98LongestInteraction() {
      var candidateInteractionIndex = Math.min(longestInteractionList.length - 1, Math.floor(getInteractionCountForNavigation() / 50));
      return longestInteractionList[candidateInteractionIndex];
    };
    /**
     * Calculates the [INP](https://web.dev/responsiveness/) value for the current
     * page and calls the `callback` function once the value is ready, along with
     * the `event` performance entries reported for that interaction. The reported
     * value is a `DOMHighResTimeStamp`.
     *
     * A custom `durationThreshold` configuration option can optionally be passed to
     * control what `event-timing` entries are considered for INP reporting. The
     * default threshold is `40`, which means INP scores of less than 40 are
     * reported as 0. Note that this will not affect your 75th percentile INP value
     * unless that value is also less than 40 (well below the recommended
     * [good](https://web.dev/inp/#what-is-a-good-inp-score) threshold).
     *
     * If the `reportAllChanges` configuration option is set to `true`, the
     * `callback` function will be called as soon as the value is initially
     * determined as well as any time the value changes throughout the page
     * lifespan.
     *
     * _**Important:** INP should be continually monitored for changes throughout
     * the entire lifespan of a page—including if the user returns to the page after
     * it's been hidden/backgrounded. However, since browsers often [will not fire
     * additional callbacks once the user has backgrounded a
     * page](https://developer.chrome.com/blog/page-lifecycle-api/#advice-hidden),
     * `callback` is always called when the page's visibility state changes to
     * hidden. As a result, the `callback` function might be called multiple times
     * during the same page load._
     */
    var onINP = function onINP(onReport, opts) {
      // RUM_CHANGE: дефолтным значением выбрана пустая функция, иначе в пререндере хрома стреляет ошибка
      var report = function report() {};
      // RUM_CHANGE: Добавлена функция возврата репортера, чтобы получать данные в произвольный момент времени
      var getCurrentReporter = function getCurrentReporter() {
        return report;
      }; // this allows manual flushing in custom builds
      // Set defaults
      opts = opts || {};
      whenActivated(function () {
        // TODO(philipwalton): remove once the polyfill is no longer needed.
        initInteractionCountPolyfill();
        metric = initMetric('INP');
        var handleEntries = function handleEntries(entries) {
          entries.forEach(function (entry) {
            if (entry.interactionId) {
              processEntry(entry);
            }
            // Entries of type `first-input` don't currently have an `interactionId`,
            // so to consider them in INP we have to first check that an existing
            // entry doesn't match the `duration` and `startTime`.
            // Note that this logic assumes that `event` entries are dispatched
            // before `first-input` entries. This is true in Chrome but it is not
            // true in Firefox; however, Firefox doesn't support interactionId, so
            // it's not an issue at the moment.
            // TODO(philipwalton): remove once crbug.com/1325826 is fixed.
            if (entry.entryType === 'first-input') {
              var noMatchingEntry = !longestInteractionList.some(function (interaction) {
                return interaction.entries.some(function (prevEntry) {
                  return entry.duration === prevEntry.duration && entry.startTime === prevEntry.startTime;
                });
              });
              if (noMatchingEntry) {
                processEntry(entry);
              }
            }
          });
          var inp = estimateP98LongestInteraction();
          if (inp && inp.latency !== metric.value) {
            metric.value = inp.latency;
            metric.entries = inp.entries;
            report();
          }
        };
        var po = observe('event', handleEntries, {
          // Event Timing entries have their durations rounded to the nearest 8ms,
          // so a duration of 40ms would be any event that spans 2.5 or more frames
          // at 60Hz. This threshold is chosen to strike a balance between usefulness
          // and performance. Running this callback for any interaction that spans
          // just one or two frames is likely not worth the insight that could be
          // gained.
          durationThreshold: opts.durationThreshold || 40
        });
        report = bindReporter(onReport, metric, opts.reportAllChanges);
        if (po) {
          // Also observe entries of type `first-input`. This is useful in cases
          // where the first interaction is less than the `durationThreshold`.
          po.observe({
            type: 'first-input',
            buffered: true
          });
          onHidden(function () {
            handleEntries(po.takeRecords());
            // If the interaction count shows that there were interactions but
            // none were captured by the PerformanceObserver, report a latency of 0.
            if (metric.value < 0 && getInteractionCountForNavigation() > 0) {
              metric.value = 0;
              metric.entries = [];
            }
            report(true);
          });
          // Only report after a bfcache restore if the `PerformanceObserver`
          // successfully registered.
          onBFCacheRestore(function () {
            resetINPState();
            // Important, we want the count for the full page here,
            // not just for the current navigation.
            prevInteractionCount = getInteractionCount();
            report = bindReporter(onReport, metric, opts.reportAllChanges);
          });
        }
      });
      return getCurrentReporter;
    };

    (function () {
      var isFinalized = false;
      var currentMetric = undefined;
      var addListener = rum._addListener || addEventListener; //костыль, чтобы работать со старой и новой версией рума

      var getCurrentReporter = onINP(function (metric) {
        currentMetric = {
          value: metric.value,
          params: {
            entriesCount: metric.entries.length,
            interactionsCount: getInteractionCount()
          },
          opts: {}
        };
        if (metric.entries.length) {
          var longestEntry = metric.entries.sort(function (a, b) {
            return b.duration - a.duration || b.processingEnd - b.processingStart - (a.processingEnd - a.processingStart);
          })[0];
          currentMetric.params.name = longestEntry.name;
          currentMetric.params.js = metric.entries.reduce(function (memo, e) {
            return memo + e.processingEnd - e.processingStart;
          }, 0);
          currentMetric.params.processTime = longestEntry.processingEnd - longestEntry.processingStart;
          currentMetric.opts.originalEndTime = longestEntry.startTime + longestEntry.duration;

          // Собираем информацию об eventTarget.
          // Не используем attribution build web vitals, т.к. он не добавляет className, если у target прописан id.
          // В итоге можем получить target="#1_k7juw01-00", по которому сложно будет понять, что это за элемент.
          // В нашей реализации getSelector всегда добавляется className элемента.
          if (longestEntry.target && rum.getSelector) {
            currentMetric.params.target = rum.getSelector(longestEntry.target);
          }
        }
      });
      function resetINP() {
        // TODO: в текущей реализации для последовательных ajax-запросов
        // не будут отправляться значения, если они одинаковые
        // проблема в том, что prevValue в bindReporter web-vitals находится в замыкании
        resetINPState();
        currentMetric = undefined;
        isFinalized = false;
      }
      function finalizeINP() {
        if (isFinalized) {
          return;
        }
        isFinalized = true;
        getCurrentReporter()(true);
        // Не отсылаем INP, если пользователь не совершал действий на странице.
        if (currentMetric === undefined) {
          return;
        }
        rum.sendDelta('inp', currentMetric.value, currentMetric.params, currentMetric.opts);
      }
      function onVisChange(event) {
        if (event.type === 'pagehide' || document.visibilityState === 'hidden') {
          finalizeINP();
        }
        return;
      }

      // Финализируем INP если страница уходит в фон, или закрывается.
      addListener('visibilitychange', onVisChange, true);
      addListener('pagehide', onVisChange, true);
      rum.finalizeINP = finalizeINP;
      rum.resetINP = resetINP;
      rum._onComplete.push(finalizeINP);
      rum._onInit.push(resetINP);
    })();

})();
